import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";

/**
 * Avmelding fra markedsfoering, paa bedriftens eget domene.
 *
 * Lenken staar nederst i SMS-er og e-poster. Foer gikk den til Convex-adressen
 * (bold-pony-700...), som ser ut som noe helt annet enn bedriften kunden
 * nettopp var hos. Siden gjoer det samme som foer: sjekker signaturen i
 * lenken og sperrer nummeret eller e-posten i Ascendly. Uten riktig
 * signatur skjer ingenting, saa ingen kan melde av andre ved aa gjette.
 *
 * Avmeldingen skjer idet siden aapnes. Loven krever at det skal vaere like
 * lett aa trekke seg som aa samtykke, og lenken er selve handlingen.
 */
const CONVEX = "https://bold-pony-700.eu-west-1.convex.cloud";
const NAVN = "Drammen Car Wash";

export const Route = createFileRoute("/avmeld")({
  head: () => ({
    meta: [{ title: `Avmelding | ${NAVN}` }, { name: "robots", content: "noindex" }],
  }),
  component: Avmeld,
});

type Status = "laster" | "ok" | "ugyldig" | "feil";

const TEKST: Record<Exclude<Status, "laster">, { tittel: string; tekst: string }> = {
  ok: {
    tittel: "Du er meldt av",
    tekst: `Du får ikke lenger markedsføring fra ${NAVN}. Har du en bestilling, får du fortsatt bekreftelse og påminnelse om selve timen.`,
  },
  ugyldig: {
    tittel: "Ugyldig lenke",
    tekst: "Denne avmeldingslenken er ikke gyldig. Åpne hele lenken fra meldingen og prøv igjen.",
  },
  feil: {
    tittel: "Det gikk ikke akkurat nå",
    tekst: "Vi fikk ikke meldt deg av. Prøv igjen om litt.",
  },
};

function Avmeld() {
  const [status, setStatus] = useState<Status>("laster");

  const kjor = useCallback(() => {
    const q = new URLSearchParams(window.location.search);
    const k = q.get("k") ?? "";
    const s = q.get("s") ?? "";
    if (!k || !s) {
      setStatus("ugyldig");
      return;
    }
    setStatus("laster");
    fetch(`${CONVEX}/api/mutation`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ path: "avmelding:avmeldFraLenke", args: { k, s }, format: "json" }),
    })
      .then((r) => r.json())
      .then((j: { status?: string; value?: { ok?: boolean } }) => {
        if (j.status !== "success" || !j.value) setStatus("feil");
        else setStatus(j.value.ok ? "ok" : "ugyldig");
      })
      .catch(() => setStatus("feil"));
  }, []);

  useEffect(kjor, [kjor]);

  return (
    <main className="flex min-h-[70vh] items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 text-center shadow-sm">
        {status === "laster" ? (
          <p className="text-muted-foreground">Melder deg av …</p>
        ) : (
          <>
            <h1 className="text-2xl font-semibold text-foreground">{TEKST[status].tittel}</h1>
            <p className="mt-3 text-muted-foreground">{TEKST[status].tekst}</p>
            {status === "feil" ? (
              <button
                type="button"
                onClick={kjor}
                className="mt-6 w-full rounded-xl bg-primary px-4 py-3 font-semibold text-primary-foreground"
              >
                Prøv igjen
              </button>
            ) : null}
          </>
        )}
      </div>
    </main>
  );
}
